import { createError, RelodeApiError } from "@relode/core";
import { candidates } from "../../../../shared/backend/controllers";
import {
    CandidateSearchActionTypes,
    creators,
} from "./candidateSearchActionTypes";
import { SearchCriteria, SearchState } from "./candidateSearchState";

export const useCandidateSearchActions = (
    state: SearchState,
    dispatch: React.Dispatch<CandidateSearchActionTypes>
) => {
    const search = async (criteria: SearchCriteria) => {
        try {
            dispatch(creators.search(criteria));
            const searchResponse = await candidates.candidateService.search(
                criteria
            );
            if (searchResponse)
                dispatch(creators.searchSuccess(searchResponse));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.searchFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.candidates.candidateSearchActions.search"
            );
        }
    };

    const sort = (field: string) => {
        dispatch(creators.sort(field));
    };

    const sortMobile = (field: string, direction: SortDir) => {
        dispatch(creators.sortMobile(field, direction));
    };

    const navigate = (direction: "next" | "prev", totalResults: number) => {
        if (direction === "next") {
            dispatch(creators.navigateNext(totalResults));
        } else {
            dispatch(creators.navigatePrev());
        }
    };

    const searchText = (searchText: string) =>
        dispatch(creators.searchText(searchText));

    const filterSingle = (
        id: string,
        name: string,
        item?: { label: string; value: string }
    ) => {
        dispatch(creators.filterSingle(id, name, item));
    };

    const filterMultiple = (
        id: string,
        name: string,
        category?: string,
        item?: { label: string; value: string }
    ) => {
        dispatch(creators.filterMultiple(id, name, category, item));
    };

    const filterBySelectedCriteria = (searchText: string) =>
        dispatch(creators.filterBySelectedCriteria(searchText));

    const addFeedback = async (
        jobId: number,
        leadId: number,
        vote: boolean,
        comment: string
    ) => {
        try {
            dispatch(creators.manageCandidateFeedback());
            await candidates.feedbackService.addFeedback(jobId, leadId, {
                vote,
                comment,
            });
            dispatch(
                creators.manageCandidateFeedbackSuccess(
                    "feedback updated",
                    jobId,
                    leadId,
                    vote,
                    comment
                )
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.manageCandidateFeedbackFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.candidates.candidateSearchActions.addFeedback"
            );
        }
    };

    const updateFeedback = async (
        jobId: number,
        leadId: number,
        vote: boolean,
        comment: string
    ) => {
        try {
            dispatch(creators.manageCandidateFeedback());
            await candidates.feedbackService.updateFeedback(jobId, leadId, {
                vote,
                comment,
            });
            dispatch(
                creators.manageCandidateFeedbackSuccess(
                    "feedback updated",
                    jobId,
                    leadId,
                    vote,
                    comment
                )
            );
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.manageCandidateFeedbackFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.candidates.candidatesSearchActions.updateFeedback"
            );
        }
    };

    const clearErrors = () => dispatch(creators.clearErrors());

    return {
        addFeedback,
        filterBySelectedCriteria,
        filterMultiple,
        filterSingle,
        navigate,
        search,
        searchText,
        sort,
        sortMobile,
        updateFeedback,
        clearErrors,
    };
};

export type CandidateSearchActions = ReturnType<
    typeof useCandidateSearchActions
>;
